<script setup lang="ts">
  import { BottomSheet, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useDebounceFn } from '@vueuse/core';
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import type { Product } from '@/entities/product';
  import FloatingChipButton from '@/shared/ui/FloatingChipButton.vue';
  import HotelCard from './HotelCard.vue';
  import ListDetailsIcon from './ListDetailsIcon.vue';
  import { MAP_MARGIN, MAP_MARGIN_WITH_CARD, PRODUCTS_DEBOUNCE_MS } from '../config/map';
  import { HOTELS_MAP_TEXTS } from '../config/texts';
  import { getCardMargin } from '../lib/mapBounds';
  import { getCheapestHotelProducts, toMapHotels } from '../lib/mapHotels';
  import { formatMapPrice } from '../lib/markerViews';
  import type { HotelOpenOptions, MapHotel, MapMargin } from '../model/types';
  import { useHotelsMapMarkers } from '../model/useHotelsMapMarkers';

  interface HotelsMapProps {
    products: Product[];
    loading?: boolean;
  }

  const props = withDefaults(defineProps<HotelsMapProps>(), { loading: false });

  interface HotelsMapEmits {
    (e: 'list'): void;
    (e: 'open', product: Product, options: HotelOpenOptions): void;
  }

  const emit = defineEmits<HotelsMapEmits>();

  const container = ref<HTMLElement | null>(null);
  const cardElement = ref<HTMLElement | null>(null);
  const selectedHotelId = ref<number | null>(null);
  const clusterHotels = ref<MapHotel[]>([]);

  const hotelProducts = computed<Product[]>(() => getCheapestHotelProducts(props.products));

  const hotels = computed<MapHotel[]>(() => toMapHotels(hotelProducts.value));

  const selectedProduct = computed<Product | null>(
    () => hotelProducts.value.find((product) => product.hotel?.id === selectedHotelId.value) ?? null,
  );

  const onSelect = (hotelId: number): void => {
    selectedHotelId.value = hotelId;
  };

  const onExpand = (expanded: MapHotel[]): void => {
    clusterHotels.value = expanded;
  };

  const { hasError, isEmpty, mount, refresh, refreshSelection, flyToHotel, setMargin } = useHotelsMapMarkers({
    container,
    getHotels: () => hotels.value,
    selectedHotelId,
    onSelect,
    onExpand,
  });

  const closeCard = (): void => {
    selectedHotelId.value = null;
  };

  const closeClusterSheet = (): void => {
    clusterHotels.value = [];
  };

  const selectFromCluster = (hotelId: number): void => {
    selectedHotelId.value = hotelId;
    clusterHotels.value = [];
  };

  const refreshProducts = useDebounceFn((): void => {
    if (selectedHotelId.value !== null && !selectedProduct.value) selectedHotelId.value = null;

    refresh();
  }, PRODUCTS_DEBOUNCE_MS);

  onMounted(() => {
    mount();
  });

  watch(() => props.products, refreshProducts);

  const measureCardMargin = (): MapMargin => {
    const mapRect = container.value?.getBoundingClientRect();
    const cardRect = cardElement.value?.getBoundingClientRect();

    return mapRect && cardRect ? getCardMargin(mapRect.bottom, cardRect.top) : MAP_MARGIN_WITH_CARD;
  };

  watch(selectedHotelId, async () => {
    refreshSelection();

    if (selectedHotelId.value === null) {
      setMargin(MAP_MARGIN);
      return;
    }

    await nextTick();
    setMargin(measureCardMargin());

    const hotel = hotels.value.find((candidate) => candidate.hotelId === selectedHotelId.value);

    if (hotel) flyToHotel(hotel);
  });
</script>

<template>
  <div
    class="hotels-map"
    data-testid="hotels-map"
  >
    <div
      ref="container"
      class="hotels-map__canvas"
      data-testid="hotels-map-canvas"
    />

    <div
      v-if="hasError || (isEmpty && !loading)"
      class="hotels-map__message"
      :data-testid="hasError ? 'hotels-map-error' : 'hotels-map-empty'"
    >
      <Typo
        :variant="TypoVariant.BodyMRegular"
        tag="span"
      >
        {{ hasError ? HOTELS_MAP_TEXTS.loadError : HOTELS_MAP_TEXTS.emptyState }}
      </Typo>
    </div>

    <div
      v-if="selectedProduct"
      ref="cardElement"
      class="hotels-map__card"
    >
      <HotelCard
        :product="selectedProduct"
        @close="closeCard"
        @open="emit('open', selectedProduct, $event)"
      />
    </div>

    <FloatingChipButton
      class="hotels-map__list-button"
      testid="hotels-map-list-button"
      @click="emit('list')"
    >
      <template #icon>
        <ListDetailsIcon />
      </template>
      {{ HOTELS_MAP_TEXTS.listButton }}
    </FloatingChipButton>

    <BottomSheet
      v-if="clusterHotels.length"
      root-class="hotels-map__cluster-sheet"
      :animated="true"
      :icon-closable="true"
      @close="closeClusterSheet"
    >
      <template #header>
        <Typo
          class="hotels-map__cluster-title"
          :variant="TypoVariant.Heading5"
          tag="span"
        >
          {{ HOTELS_MAP_TEXTS.clusterTitle }}
        </Typo>
      </template>

      <ul class="hotels-map__cluster-list">
        <li
          v-for="hotel in clusterHotels"
          :key="hotel.hotelId"
        >
          <button
            class="hotels-map__cluster-item"
            type="button"
            :data-testid="`hotels-map-cluster-item-${hotel.hotelId}`"
            @click="selectFromCluster(hotel.hotelId)"
          >
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ hotel.name }}
            </Typo>
            <Typo
              class="hotels-map__cluster-price"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ formatMapPrice(hotel.price) }}
            </Typo>
          </button>
        </li>
      </ul>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .hotels-map {
    background: var(--color-bg-secondary);
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;

    &__canvas {
      flex: 1 1 auto;
      isolation: isolate;
      min-height: 0;
      width: 100%;
    }

    &__message {
      align-items: center;
      box-sizing: border-box;
      color: var(--color-text-secondary);
      display: flex;
      inset: 0;
      justify-content: center;
      padding: var(--spacing-m);
      position: absolute;
      text-align: center;
    }

    &__card {
      bottom: calc(var(--spacing-m) * 2 + 40px + env(safe-area-inset-bottom, 0px));
      left: var(--spacing-l);
      position: absolute;
      right: var(--spacing-l);
      z-index: 2;
    }

    &__list-button {
      position: absolute;
    }

    &__cluster-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__cluster-item {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      padding: var(--spacing-xs) 0;
      width: 100%;
    }

    &__cluster-price {
      color: var(--color-text-secondary);
    }

    // stylelint-disable-next-line selector-class-pattern -- ymaps3 ставит копирайт и «Открыть Яндекс Карты» в одну строку, на телефоне они перекрываются
    &__canvas ::v-deep .ymaps3--controls_top.ymaps3--controls_left {
      top: var(--spacing-xxl);
    }

    &__canvas ::v-deep .hotels-map__chip {
      align-items: center;
      background: var(--color-slate-70);
      border-radius: var(--rounding-xxl);
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      font-family: var(--font-family);
      justify-content: center;
      left: 0;
      min-height: 40px;
      min-width: 40px;
      padding: 0 var(--spacing-m);

      // ymaps3 растягивает обёртку маркера по чипу, без absolute её невидимый прямоугольник перехватывает тапы соседей
      position: absolute;
      top: 0;
      transform: translate(-50%, -50%);
      white-space: nowrap;
    }

    &__canvas ::v-deep .hotels-map__chip_selected {
      background: linear-gradient(
        238deg,
        var(--color-yellow-40) 0%,
        var(--color-orange-40) 17%,
        var(--color-orange-70) 100%
      );
      box-shadow: var(--shadow-l);
    }

    &__canvas ::v-deep .hotels-map__chip_contains-selected {
      outline: 2px solid var(--color-brand-primary);
    }

    &__canvas ::v-deep .hotels-map__chip-price,
    &__canvas ::v-deep .hotels-map__chip-count {
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
    }
  }
</style>
